import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Alert,
  Paper,
} from '@mui/material'
import {
  Visibility as ViewIcon,
  Add as AddIcon,
} from '@mui/icons-material'
import { request } from '../common/services/request'
import { API_ENDPOINTS } from '../common/endpoints'
import { ROUTES, getPatientDetailRoute } from '../common/constant/routes'
import type { Patient, PatientListResponse } from '../common/types'

function PatientList() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [patients, setPatients] = useState<Patient[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(20)

  const calculateAge = (dob: string | null): string => {
    if (!dob) return '-'
    const birthDate = new Date(dob)
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    return age.toString()
  }

  const fetchPatients = async () => {
    try {
      setLoading(true)
      const client = request()
      const params: any = { page: page + 1, page_size: pageSize }

      const res = await client.get<PatientListResponse>(API_ENDPOINTS.PATIENTS.BASE, { params })
      setPatients(res.data.items)
      setTotal(res.data.total)
    } catch (e: any) {
      setError(e.message || 'Failed to load patients')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPatients()
  }, [page, pageSize])

  const formatPhone = (phone: string | null): string => {
    if (!phone) return '-'
    // Extract extension (e.g., x123 or ext123)
    const extMatch = phone.match(/(?:ext\.?|x)\s*(\d+)$/i)
    const ext = extMatch ? extMatch[1] : null
    // Keep digits only for formatting
    const digits = phone.replace(/[^0-9]/g, '')
    if (digits.length === 11 && digits.startsWith('1')) {
      // US with leading 1
      const area = digits.slice(1, 4)
      const p1 = digits.slice(4, 7)
      const p2 = digits.slice(7)
      return `(+1) ${area}-${p1}-${p2}${ext ? ` x${ext}` : ''}`
    }
    if (digits.length === 10) {
      const area = digits.slice(0, 3)
      const p1 = digits.slice(3, 6)
      const p2 = digits.slice(6)
      return `(${area}) ${p1}-${p2}${ext ? ` x${ext}` : ''}`
    }
    // Fallback: group last 4 digits and show rest
    if (digits.length > 4) {
      const last = digits.slice(-4)
      const rest = digits.slice(0, -4)
      return `${rest}-${last}${ext ? ` x${ext}` : ''}`
    }
    return phone
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
            Patients
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Patient records from OpenEMR
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(ROUTES.PATIENT_CREATE)}
        >
          Create Patient
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Sex</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>SS</TableCell>
                  <TableCell>DOB</TableCell>
                  <TableCell>PID</TableCell>
                  <TableCell>Age</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(patients?.length ?? 0) === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="textSecondary">No patients found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  patients.map((patient) => (
                    <TableRow 
                      key={patient.uuid} 
                      hover
                      onClick={() => navigate(getPatientDetailRoute(patient.uuid))}
                      sx={{ cursor: 'pointer', '& .MuiTableCell-root': { py: 0.5 } }}
                    >
                      <TableCell>
                        {patient.fname} {patient.lname}
                      </TableCell>
                      <TableCell>{patient.sex || '-'}</TableCell>
                      <TableCell>{formatPhone(patient.phone_home || patient.phone_cell)}</TableCell>
                      <TableCell>{patient.ss || patient.pubpid || '-'}</TableCell>
                      <TableCell>{patient.DOB || '-'}</TableCell>
                      <TableCell>{patient.pid}</TableCell>
                      <TableCell>{calculateAge(patient.DOB)}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(getPatientDetailRoute(patient.uuid))
                          }}
                          title="View Details / Generate"
                        >
                          <ViewIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10))
                setPage(0)
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </>
        )}
      </TableContainer>
    </Box>
  )
}

export default PatientList
